import { IncomingHttpHeaders } from "http2";
import { isEqual } from "lodash";
import { DateTime } from "luxon";
import { HeadersInit } from "node-fetch";
import { RouterInferenceHelpers } from "../src/api/trpc/rootRouter";
import { ServerReminderDescriptor } from "../src/entity/ServerReminder";
import { Constants } from "./constants";
import {
	AttendeeRoleDataWithIDs,
	CommandPermissions,
	DiscordAuthToken,
	GuildSettingsSectionType,
	IGetGuildDefaultOptionsFromDMArgs,
	RawEventRSVPOption,
	WebDashboardType,
} from "./sesh_types";
import { common_format_utils } from "./common_format_utils";
import { time_format_utils } from "./time_format_utils";

export type RemoveVoid<T> = T extends void ? never : T;

export type RedirectToCheckoutInput = RemoveVoid<
	RouterInferenceHelpers["premium"]["getStripeCheckoutRedirect"]["input"]
>;

export type StripeQueryURLParams =
	| {
			type: "checkout";
			subscription_span: string;
			should_force_no_coupon: "true" | "false";
			product_id: string;
			guild_id?: string;
	  }
	| { type: "manager" };

export namespace common_utils {
	export function createSignupPresetsURLState(
		client_id: string,
		dm_data?: IGetGuildDefaultOptionsFromDMArgs
	) {
		const state = {
			client_id: client_id,
			dashboard_type: WebDashboardType.Signup_Presets,
			dm_data: dm_data ?? undefined,
		};

		return encode_state(state);
	}

	export function limitStringLength(
		str: string,
		characters: number,
		suffix = ""
	) {
		if (str.length <= characters) {
			return str;
		}

		return str.substring(0, characters - suffix.length) + suffix;
	}

	/**
	 * TODO: handle escaping the placeholder
	 */
	export function replaceEventNamePlaceholderForThread(
		str: string,
		event_name: string
	) {
		return str.replaceAll(Constants.EVENT_NAME_PLACEHOLDER, event_name);
	}

	export type PlaceholderFuncContext = {
		event_name: string | undefined;
		event_link: string | undefined;
		start_time: luxon.DateTime | undefined;
		timezone: string | undefined;
		duration_ms: number | undefined;
		description: string | undefined;
		rsvp_options: RawEventRSVPOption[] | undefined;
	};

	export type TitlePlaceholderFuncContext = Omit<
		PlaceholderFuncContext,
		"description"
	>;

	type NoUndefinedField<T> = {
		[P in keyof T]-?: NonNullable<T[P]>;
	};

	type TransformerDescriptor<
		T extends keyof NoUndefinedField<PlaceholderFuncContext>
	> = {
		transformer: (
			ctx: Pick<NoUndefinedField<PlaceholderFuncContext>, T>
		) => string;
		description: string;
		not_allowed_in_title?: boolean;
		requires: T[];
	};

	function CreatePlaceholderTransformerDescriptor<
		T extends keyof PlaceholderFuncContext
	>(t: TransformerDescriptor<T>) {
		return t;
	}

	export const PlaceholderTokens: Record<
		`$${string}`,
		ReturnType<typeof CreatePlaceholderTransformerDescriptor>
	> = {
		$EventName: CreatePlaceholderTransformerDescriptor({
			requires: ["event_name"],
			description: "Name of event",
			transformer: (ctx) => ctx.event_name,
		}),
		$EventLink: CreatePlaceholderTransformerDescriptor({
			requires: ["event_link"],
			description: "Link to event signup",
			not_allowed_in_title: true,
			transformer: (ctx) => ctx.event_link,
		}),
		$EventDescription: CreatePlaceholderTransformerDescriptor({
			requires: ["description"],
			description: "Description of event",
			not_allowed_in_title: true,
			transformer: (ctx) => ctx.description,
		}),
		$EventRSVPCounts: CreatePlaceholderTransformerDescriptor({
			requires: ["rsvp_options"],
			description: "All RSVP options with counts (e.g. ✅ 5  ❌ 2)",
			not_allowed_in_title: true,
			transformer: ({ rsvp_options }) => {
				return rsvp_options
					.map((option) => {
						const emoji_string = option.custom_emoji_data
							? common_format_utils.get_stringified_emoji_for_embed(
									option.custom_emoji_data
							  )
							: option.emoji;

						const count_string = option.limit
							? `${option.user_ids?.length ?? 0}/${option.limit}`
							: `${option.user_ids?.length ?? 0}`;
						return `${emoji_string} ${count_string}`;
					})
					.join("    ");
			},
		}),
		$TS_FullLong: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Verbose localized start datetime",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:F>`,
		}),
		$TS_FullShort: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Concise localized start datetime",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:f>`,
		}),
		$TS_DateLong: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Verbose localized start date",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:D>`,
		}),
		$TS_DateShort: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Concise localized start date",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:d>`,
		}),
		$TS_TimeLong: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Verbose localized start time",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:T>`,
		}),
		$TS_TimeShort: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Concise localized start time",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:t>`,
		}),
		$TS_Relative: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Localized relative start time",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:R>`,
		}),
		$TS_DateTimeShort: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Short localized datetime",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:s>`,
		}),
		$TS_DateTimeWithSeconds: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Short localized datetime with seconds",
			not_allowed_in_title: true,
			transformer: ({ start_time }) =>
				`<t:${start_time.toSeconds().toFixed(0)}:S>`,
		}),
		$DT_DateLong: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Verbose start date",
			transformer: (ctx) =>
				ctx.start_time.toLocaleString({
					month: "long",
					year: "numeric",
					day: "2-digit",
				}),
		}),
		$DT_DateMDY: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event month/day/year",
			transformer: ({ start_time }) =>
				start_time.toLocaleString(DateTime.DATE_SHORT),
		}),
		$DT_DateDMY: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event day/month/year",
			transformer: ({ start_time }) =>
				start_time.toLocaleString(DateTime.DATE_SHORT, {
					locale: "en-GB",
				}),
		}),
		$DT_Time: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event time in 12 hour format",
			transformer: ({ start_time }) =>
				start_time.toLocaleString({
					hour: "numeric",
					minute: "numeric",
				}),
		}),
		$DT_Time24: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event time in 24 hour format",
			transformer: ({ start_time }) =>
				start_time.toLocaleString({
					hour: "numeric",
					minute: "numeric",
					hour12: false,
				}),
		}),
		$DT_MonthName: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event date month name",
			transformer: ({ start_time }) =>
				start_time.toLocaleString({ month: "long" }),
		}),
		$DT_Weekday: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event weekday name",
			transformer: ({ start_time }) =>
				start_time.toLocaleString({ weekday: "long" }),
		}),
		$DT_MonthNum: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event number of the month",
			transformer: ({ start_time }) =>
				start_time.toLocaleString({ month: "numeric" }),
		}),
		$DT_DayNum: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event number of day of month",
			transformer: ({ start_time }) =>
				start_time.toLocaleString({ day: "numeric" }),
		}),
		$DT_YearNum: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Event year",
			transformer: ({ start_time }) =>
				start_time.toLocaleString({ year: "numeric" }),
		}),
		$DT_TimezoneShort: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Server timezone concise",
			transformer: ({ start_time }) =>
				start_time.zone.offsetName(start_time.toMillis(), {
					format: "short",
				}),
		}),
		$DT_TimezoneLong: CreatePlaceholderTransformerDescriptor({
			requires: ["start_time"],
			description: "Server timezone verbose",
			transformer: ({ start_time }) =>
				start_time.zone.offsetName(start_time.toMillis(), {
					format: "long",
				}),
		}),
		$DT_TimeRelative: CreatePlaceholderTransformerDescriptor({
			requires: ["duration_ms"],
			description: "Event relative start time",
			transformer: ({ duration_ms }) => {
				return time_format_utils.formatDurationNew(
					duration_ms,
					1000 * 45
				);
			},
		}),
	};

	export const SortedPlaceholderTokensByLength = Object.keys(
		PlaceholderTokens
	).sort((a, b) => b.length - a.length);

	export type PlaceholderType =
		| "EmbedTitle"
		| "ThreadTitle"
		| "EmbedDescription";

	const CharacterLimits: Record<PlaceholderType, number> = {
		EmbedTitle: Constants.EMBED_TITLE_LIMIT,
		ThreadTitle: Constants.THREAD_TITLE_LIMIT,
		EmbedDescription: Constants.DESCRIPTION_CHAR_LIMIT,
	};

	export function replacePlaceholders<T extends PlaceholderType>(
		type: T,
		str: string,
		values: T extends "EmbedTitle" | "ThreadTitle"
			? TitlePlaceholderFuncContext
			: PlaceholderFuncContext
	) {
		let result = str;

		const date = values.start_time
			?.setZone(values.timezone)
			.setLocale("en-US");

		const context: PlaceholderFuncContext = {
			...values,
			start_time: date,
			description:
				"description" in values ? values.description : undefined,
		};

		for (const key of SortedPlaceholderTokensByLength) {
			const descriptor =
				PlaceholderTokens[key as keyof typeof PlaceholderTokens];

			if (
				descriptor.not_allowed_in_title &&
				(type === "EmbedTitle" || type === "ThreadTitle")
			) {
				continue;
			}

			let should_continue = false;
			for (const required_field of descriptor.requires) {
				// We want duration_ms === 0 to be allowed

				// Edge case for valid description, since "" evaluates to false
				const is_valid_description =
					required_field === "description" &&
					context[required_field] != null;

				if (
					!is_valid_description &&
					!context[required_field] &&
					context[required_field] !== 0
				) {
					console.log("continuing");
					should_continue = true;
					break;
				}
			}

			if (should_continue) {
				continue;
			}

			result = result.replaceAll(
				key,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				descriptor.transformer(context as unknown as any)
			);
		}

		return limitStringLength(result, CharacterLimits[type]);
	}

	export function areAttendeeRoleDatasEqual(
		a: AttendeeRoleDataWithIDs,
		b: AttendeeRoleDataWithIDs
	) {
		const are_both_null =
			(a == null || a.role_ids?.length === 0) &&
			(b == null || b?.role_ids.length === 0);

		return are_both_null || isEqual(a, b);
	}

	/**
	 * Treats null and undefined as equal to one another
	 */
	export function areObjectsEqual<T extends Object>(
		obj_a: T | null,
		obj_b: T | null
	) {
		return isEqual(obj_a, obj_b) || obj_a == obj_b;
	}

	export function ValidateReminderWithIndex(
		all_reminders: ServerReminderDescriptor[],
		current_index: number,
		event_channel_id?: string,
		event_start_time?: Date
	): {
		is_valid: boolean;
		error_message?: string;
	} {
		const current = all_reminders[current_index];
		const others = all_reminders.filter(
			(val, index) => index !== current_index
		);

		return ValidateReminder(
			current,
			others,
			event_channel_id,
			event_start_time
		);
	}

	export function ValidateReminder(
		current: ServerReminderDescriptor,
		others: ServerReminderDescriptor[],
		event_channel_id?: string,
		_event_start_time?: Date
	): { is_valid: boolean; error_message?: string } {
		if (!current.message_description && !current.message_title) {
			return {
				is_valid: false,
				error_message: "Both title and description can not be empty",
			};
		}

		if (
			others.some((reminder) => {
				const is_same_time =
					reminder.fire_before_ms === current.fire_before_ms;
				const is_same_desired_channel =
					reminder.desired_channel_id == current.desired_channel_id;

				const is_same_event_channel =
					(!reminder.desired_channel_id &&
						current.desired_channel_id === event_channel_id) ||
					(!current.desired_channel_id &&
						reminder.desired_channel_id === event_channel_id);

				return (
					is_same_time &&
					(is_same_desired_channel ||
						(event_channel_id && is_same_event_channel))
				);
			})
		) {
			return {
				is_valid: false,
				error_message:
					"Can not have two reminders in the same channel at the same time",
			};
		}

		return { is_valid: true };
	}

	export function formatCommandPermissionsForDB(
		command_permissions: CommandPermissions[],
		guild_id: string
	) {
		const result = {};

		for (const command_data of command_permissions) {
			let has_everyone = false;
			let mapped_ids = command_data.roles_ids.map((val) => {
				if (val === "@everyone") {
					has_everyone = true;
				}
				return val;
			});

			if (has_everyone) {
				mapped_ids = [guild_id];
			}

			result[command_data.command] = mapped_ids;
		}

		return result;
	}

	export function formatCommandPermissionsForWeb(
		permissions_raw: any,
		guild_id: string,
		valid_ids?: string[]
	) {
		const result: CommandPermissions[] = [];

		if (!permissions_raw) {
			permissions_raw = {};
		}

		for (const command of Constants.SESH_PERMISSIONS) {
			const value = permissions_raw[command];
			if (value) {
				const mapped_values: string[] = [];
				for (const id of value) {
					if (id === guild_id) {
						mapped_values.push("@everyone");
					} else if (!valid_ids || valid_ids.includes(id)) {
						mapped_values.push(id);
					}
				}

				result.push({
					command: command,
					roles_ids: mapped_values,
				});
			} else {
				result.push({
					command: command,
					roles_ids: Constants.COMMAND_DEFAULT_EVERYONE_PERMISSION[
						command
					]
						? ["@everyone"]
						: [],
				});
			}
		}

		return result;
	}

	export function encode_state(state: Object) {
		const data = JSON.stringify(state);
		const buff = Buffer.from(data);
		return encodeURIComponent(buff.toString("base64"));
	}

	export type TemplateQueryParamType = "event" | "template";

	export function getEventCreateURL(
		server_id: string,
		channel_id?: string,
		template?: {
			id: string;
			type: TemplateQueryParamType;
		},
		extra_params: Record<string, string> = {}
	) {
		const params: string[] = [];

		if (channel_id) {
			params.push(`channel_id=${channel_id}`);
		}
		if (template) {
			params.push(`template_id=${template.type}_${template.id}`);
		}

		for (const key in extra_params) {
			params.push(`${key}=${extra_params[key]}`);
		}

		return `/dashboard/${server_id}/events/create?${params.join("&")}`;
	}

	export function getEventListURL(server_id: string) {
		return `/dashboard/${server_id}/events`;
	}

	export function getAttendeesDisplayURL(
		server_id: string,
		message_id: string,
		from_edit = false
	) {
		const suffix = from_edit ? "?source=edit" : "";

		return `/dashboard/${server_id}/events/attendees/${message_id}${suffix}`;
	}

	export function getPollListURL(server_id: string) {
		return `/dashboard/${server_id}/polls`;
	}

	export function getEventEditURL(server_id: string, message_id: string) {
		return `/dashboard/${server_id}/events/edit/${message_id}`;
	}

	export function getPollCreateURL(server_id: string, channel_id?: string) {
		let channel_id_param = "";
		if (channel_id) {
			channel_id_param = `?channel_id=${channel_id}`;
		}
		return `/dashboard/${server_id}/polls/create${channel_id_param}`;
	}

	export function getPollEditURL(server_id: string, message_id: string) {
		return `/dashboard/${server_id}/polls/edit/${message_id}`;
	}

	export function getPollResultsURL(server_id: string, message_id: string) {
		return `/dashboard/${server_id}/polls/results/${message_id}`;
	}

	export function getGuildSettingsURL(
		server_id: string,
		section?: Lowercase<GuildSettingsSectionType>,
		query?: Record<string, string>
	) {
		if (section) {
			if (query) {
				return `/dashboard/${server_id}/settings?section=${section}&${new URLSearchParams(
					query
				).toString()}`;
			}
			return `/dashboard/${server_id}/settings?section=${section}`;
		}

		return `/dashboard/${server_id}/settings`;
	}

	export function getGuildCalendarSettingsURL(server_id: string) {
		return `/dashboard/${server_id}/settings/calendar`;
	}

	export function getGuildPublicCalendarURL(server_id: string) {
		return `/calendar/${server_id}`;
	}

	export function getGuildChannelPickerURL(server_id: string) {
		return `/dashboard/${server_id}/settings/channels`;
	}

	export function getGuildChannelSettingsURL(
		server_id: string,
		channel_id: string
	) {
		return `/dashboard/${server_id}/settings/channels/${channel_id}`;
	}

	export function getGuildOptionsURL(server_id: string) {
		return `/dashboard/${server_id}`;
	}

	export function getTimeFinderURL(opts: {
		server_id: string;
		type: "create" | "view" | "base" | "edit";
		just_created_view?: boolean;
		instance_id?: string;
		is_public?: boolean;
	}) {
		const { server_id, type, instance_id, is_public } = opts;

		let type_str = "";
		let query_params = "";

		// Add just_created query parameter if needed
		if (opts.just_created_view && type === "view" && instance_id) {
			query_params = "?just_created=true";
		}

		if (is_public) {
			switch (type) {
				case "create":
					type_str = "";
					break;
				case "view":
					type_str = `view/${opts.instance_id}`;
					break;
				default:
					throw new Error("Invalid type");
			}

			return `/time-finder/${type_str}${query_params}`;
		}

		if (!server_id) {
			throw new Error("Server ID is required");
		}

		switch (type) {
			case "base":
				type_str = "";
				break;
			case "create":
				type_str = "create";
				break;
			case "view":
				type_str = instance_id ? `view/${instance_id}` : "view";
				break;
			case "edit":
				type_str = `edit/${instance_id}`;
				break;
		}

		return `/dashboard/${server_id}/time-finder/${type_str}${query_params}`;
	}

	export function getPremiumPageURL() {
		return `/dashboard/premium`;
	}

	export function getStripeRedirectURL(params: StripeQueryURLParams) {
		const search_params = new URLSearchParams();

		for (const key in params) {
			search_params.append(key, params[key]);
		}

		return `/dashboard/premium/stripe?${search_params}`;
	}

	export function createSeshHeader(discord_auth_token: DiscordAuthToken) {
		const headers: HeadersInit = {
			"Content-Type": "application/json",
			Authorization: `${discord_auth_token.token_type} ${discord_auth_token.access_token}`,
		};

		return headers;
	}

	export function markdownToHtml(markdown: string) {
		// Bold
		let result = markdown.replace(/\*\*([^*]+?)\*\*/g, "<b>$1</b>");

		// Links
		result = result.replace(
			/\[([^\]]+)\]\(([^)]+)\)/g,
			'<a href="$2" class="manual-link" target="_blank">$1</a>'
		);

		// Command example
		result = result.replace(
			/`([^`]+?)`/g,
			'<span class="command-example">$1</span>'
		);

		result = result.replace(/\n/g, "<br />");

		return result;
	}

	export function parseSeshHeader(headers: IncomingHttpHeaders) {
		if (!headers.authorization) {
			throw new Error("No authorization header");
		}

		const authorization_split = headers.authorization.split(" ");
		const token_type = authorization_split[0];
		const access_token = authorization_split[1];
		return {
			token_type,
			access_token,
		};
	}
}
